import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Avatar, IconButton, Divider, Paper, CircularProgress, Alert, Collapse } from '@mui/material';
import { styled } from '@mui/material/styles';
import ThumbUpOutlinedIcon from '@mui/icons-material/ThumbUpOutlined';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import ReplyIcon from '@mui/icons-material/Reply';
import CloseIcon from '@mui/icons-material/Close';
import { formatDistanceToNow } from 'date-fns';

import { useAuth } from '../../contexts/AuthContext';
import { 
  PhotoComment, 
  getPhotoComments, 
  addPhotoComment, 
  updatePhotoComment, 
  deletePhotoComment,
  toggleCommentLike
} from '../../services/photoCommentService';

const CommentPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '&.reply': {
    marginLeft: theme.spacing(6),
    marginTop: theme.spacing(-1),
    backgroundColor: theme.palette.background.default,
  }
}));

const CommentHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const CommentActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(1),
  '& > *': {
    marginRight: theme.spacing(1),
  }
}));

interface PhotoCommentsProps {
  photoId: string;
  tripId: string;
}

export const PhotoComments: React.FC<PhotoCommentsProps> = ({ photoId, tripId }) => {
  const { currentUser } = useAuth();
  const [comments, setComments] = useState<PhotoComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [commentText, setCommentText] = useState('');
  const [replyTo, setReplyTo] = useState<string | null>(null);
  const [editingComment, setEditingComment] = useState<string | null>(null);
  const [editText, setEditText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const loadComments = async () => {
      try {
        setLoading(true);
        setError(null);
        const fetchedComments = await getPhotoComments(photoId);
        setComments(fetchedComments);
      } catch (err) {
        console.error('Error loading comments:', err);
        setError('Failed to load comments. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadComments();
  }, [photoId]);

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim() || !currentUser) return;

    try {
      setSubmitting(true);
      const newComment = await addPhotoComment({
        photoId,
        tripId,
        text: commentText.trim(),
        replyToId: replyTo || undefined
      });

      // Add the new comment to the list
      setComments(prevComments => [...prevComments, newComment]);
      
      // Reset form
      setCommentText('');
      setReplyTo(null);
    } catch (err: any) {
      setError(err.message || 'Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId: string) => {
    if (!editText.trim()) return;
    
    try {
      setSubmitting(true);
      await updatePhotoComment(commentId, { text: editText.trim() });
      
      // Update the comment in the list
      setComments(prevComments => 
        prevComments.map(comment => 
          comment.id === commentId 
            ? { 
                ...comment, 
                text: editText.trim(), 
                updatedAt: new Date(),
                isEdited: true 
              } 
            : comment
        )
      );
      
      // Reset edit state
      setEditingComment(null);
      setEditText('');
    } catch (err: any) {
      setError(err.message || 'Failed to update comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    
    try {
      setSubmitting(true);
      await deletePhotoComment(commentId);
      
      // Find if the comment being deleted is a parent comment
      const isParentComment = comments.some(c => c.id === commentId && !c.replyToId);
      
      if (isParentComment) {
        // Remove the parent comment and all replies
        setComments(prevComments => 
          prevComments.filter(comment => 
            comment.id !== commentId && comment.replyToId !== commentId
          )
        );
      } else {
        // Just remove this single comment
        setComments(prevComments => 
          prevComments.filter(comment => comment.id !== commentId)
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to delete comment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleLikeComment = async (commentId: string) => {
    if (!currentUser) return;
    
    try {
      await toggleCommentLike(commentId);
      
      // Update the comment likes in the UI
      setComments(prevComments => 
        prevComments.map(comment => {
          if (comment.id === commentId) {
            const userLiked = comment.likes.includes(currentUser.uid);
            return {
              ...comment,
              likes: userLiked
                ? comment.likes.filter(uid => uid !== currentUser.uid)
                : [...comment.likes, currentUser.uid]
            };
          }
          return comment;
        })
      );
    } catch (err: any) {
      setError(err.message || 'Failed to like comment');
    }
  };

  const startReply = (commentId: string) => {
    setReplyTo(commentId);
    setEditingComment(null);
  };

  const startEdit = (comment: PhotoComment) => {
    setEditingComment(comment.id);
    setEditText(comment.text);
    setReplyTo(null);
  };

  const cancelAction = () => {
    setReplyTo(null);
    setEditingComment(null);
    setEditText('');
  };

  const renderComment = (comment: PhotoComment, isReply = false) => {
    const isCurrentUserComment = currentUser && comment.userId === currentUser.uid;
    const userLiked = currentUser && comment.likes.includes(currentUser.uid);
    
    return (
      <CommentPaper key={comment.id} className={isReply ? 'reply' : ''} elevation={isReply ? 1 : 3}>
        <CommentHeader>
          <Avatar 
            src={comment.userPhotoURL} 
            alt={comment.userDisplayName}
            sx={{ width: 36, height: 36, marginRight: 1 }}
          />
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {comment.userDisplayName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
              {comment.isEdited && ' (edited)'}
            </Typography>
          </Box>
        </CommentHeader>
        
        {editingComment === comment.id ? (
          <Box component="form" onSubmit={(e) => { e.preventDefault(); handleEditComment(comment.id); }}>
            <TextField
              fullWidth
              multiline
              rows={2}
              value={editText}
              onChange={(e) => setEditText(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={cancelAction}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                size="small" 
                type="submit"
                disabled={submitting || !editText.trim()}
              >
                {submitting ? <CircularProgress size={20} /> : 'Save'}
              </Button>
            </Box>
          </Box>
        ) : (
          <>
            <Typography variant="body1">{comment.text}</Typography>
            
            <CommentActions>
              <IconButton 
                size="small" 
                onClick={() => handleLikeComment(comment.id)}
                color={userLiked ? 'primary' : 'default'}
              >
                {userLiked ? <ThumbUpIcon fontSize="small" /> : <ThumbUpOutlinedIcon fontSize="small" />}
                {comment.likes.length > 0 && (
                  <Typography variant="caption" sx={{ ml: 0.5 }}>
                    {comment.likes.length}
                  </Typography>
                )}
              </IconButton>
              
              {!isReply && currentUser && (
                <Button 
                  size="small" 
                  startIcon={<ReplyIcon />} 
                  onClick={() => startReply(comment.id)}
                >
                  Reply
                </Button>
              )}
              
              {isCurrentUserComment && (
                <>
                  <IconButton size="small" onClick={() => startEdit(comment)}>
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDeleteComment(comment.id)}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </>
              )}
            </CommentActions>
          </>
        )}
        
        {/* Show the reply form if user is replying to this comment */}
        {replyTo === comment.id && (
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              placeholder="Write a reply..."
              multiline
              rows={2}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              variant="outlined"
              size="small"
              sx={{ mb: 1 }}
            />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Button 
                variant="text" 
                size="small" 
                onClick={cancelAction}
                disabled={submitting}
              >
                Cancel
              </Button>
              <Button 
                variant="contained" 
                size="small" 
                onClick={handleSubmitComment}
                disabled={submitting || !commentText.trim()}
              >
                {submitting ? <CircularProgress size={20} /> : 'Reply'}
              </Button>
            </Box>
          </Box>
        )}
      </CommentPaper>
    );
  };

  // Group comments by parent/reply
  const parentComments = comments.filter(comment => !comment.replyToId);
  const replyComments = comments.filter(comment => comment.replyToId);

  const getCommentWithReplies = (parentComment: PhotoComment) => {
    return [
      renderComment(parentComment),
      ...replyComments
        .filter(reply => reply.replyToId === parentComment.id)
        .map(reply => renderComment(reply, true))
    ];
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Comments {comments.length > 0 && `(${comments.length})`}
      </Typography>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 2 }}
          action={
            <IconButton size="small" onClick={() => setError(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {error}
        </Alert>
      )}

      {/* Comment input for new top-level comments */}
      {currentUser && !replyTo && !editingComment && (
        <Box sx={{ mb: 3 }}>
          <TextField
            fullWidth
            placeholder="Add a comment..."
            multiline
            rows={3}
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            variant="outlined"
            sx={{ mb: 1 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="contained" 
              onClick={handleSubmitComment}
              disabled={submitting || !commentText.trim()}
            >
              {submitting ? <CircularProgress size={24} /> : 'Post Comment'}
            </Button>
          </Box>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Typography color="text.secondary" align="center" sx={{ py: 3 }}>
          Be the first to comment on this photo!
        </Typography>
      ) : (
        <Box>
          {parentComments.map(parentComment => (
            <React.Fragment key={parentComment.id}>
              {getCommentWithReplies(parentComment)}
            </React.Fragment>
          ))}
        </Box>
      )}
    </Box>
  );
}; 